import { inject, Injectable, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, lastValueFrom, Subscription } from 'rxjs';
import { Condition, SellNowFormStep } from 'src/app/enums';
import { EnumHelpers } from 'src/app/helpers';
import { Accessory, ListItemProduct, SellNowSteps } from 'src/app/interfaces';
import { environment } from 'src/environments/environment';
import { SellNowService } from '../api';
import { FileService } from './file.service';
import { ActivatedRoute } from '@angular/router';

interface AccessoryGroup {
    label: FormControl<string>;
    isSelected: FormControl<boolean>;
}

@Injectable({
    providedIn: 'root'
})
export class AppSellNowService implements OnInit {
    form: FormGroup;
    productInfoForm: FormGroup;
    imagesForm: FormGroup;
    serviceApi = inject(SellNowService);
    fileService = inject(FileService);
    conditionsList: Condition[] = [];
    currentStep = SellNowFormStep.InfoProduct;

    displayErrorsInfoForm = false;
    displayErrorsImagesForm = false;
    valueChangesSubscription: Subscription;
    recentProducts: ListItemProduct[] = [];

    gdprVersion: string;
    tcVersion: string;

    saveAsDraft = false;

    private route = inject(ActivatedRoute);

    constructor() {
        // can have an initial loader here
        this.conditionsList = EnumHelpers.getValuesAsString(Condition);
        this.initForm();
    }

    ngOnInit(): void {}

    async getSteps() {
        try {
            const steps = await lastValueFrom(this.serviceApi.getSteps());
            this.recentProducts = steps.recentProducts;
            this.currentStep = steps.currentStep;
            this.gdprVersion = steps.gdprVersion;
            this.tcVersion = steps.tcVersion;
            this.setApiFormData(steps);
        } catch (error) {
            console.log('Error getting steps', error);
        }
    }

    private setApiFormData(steps: SellNowSteps) {
        if (this.valueChangesSubscription) {
            this.saveAsDraft = false;
            this.valueChangesSubscription.unsubscribe();
        }
        this.updateForm(steps);
        this.addAccessories(steps.productInfo?.accessories);
        this.addAdditionalImages(steps.images?.additional);
        this.valueChangesSubscription = this.form.valueChanges.subscribe((value) => {
            this.saveAsDraft = true;
        });
    }

    private initForm() {
        this.productInfoForm = new FormGroup({
            firstName: new FormControl(undefined, { validators: [Validators.required] }),
            lastName: new FormControl(undefined, { validators: [Validators.required] }),
            phone: new FormControl(undefined, { validators: [Validators.required] }),
            brand: new FormControl(undefined, { validators: [Validators.required] }),
            condition: new FormControl(undefined, { validators: [Validators.required] }),
            model: new FormControl(undefined, { validators: [Validators.required] }),
            price: new FormControl(undefined, { validators: [Validators.required] }),
            accessories: new FormArray<FormGroup<AccessoryGroup>>([])
        });

        this.imagesForm = new FormGroup({
            front: new FormControl(undefined, { validators: [Validators.required] }),
            back: new FormControl(undefined),
            bottom: new FormControl(undefined),
            interior: new FormControl(undefined),
            additional: new FormArray<FormControl<string>>([])
        });

        this.form = new FormGroup({
            id: new FormControl(undefined),
            referralCode: new FormControl(undefined),
            currentStep: new FormControl(undefined),
            productInfo: this.productInfoForm,
            images: this.imagesForm
        });
    }

    private updateForm(steps?: SellNowSteps) {
        this.productInfoForm.patchValue({
            firstName: steps?.productInfo?.firstName ?? undefined,
            lastName: steps?.productInfo?.lastName ?? undefined,
            phone: steps?.productInfo?.phone ?? undefined,
            brand: steps?.productInfo?.brand ?? undefined,
            condition: steps?.productInfo?.condition ?? undefined,
            model: steps?.productInfo?.model ?? undefined,
            price: steps?.productInfo?.price ?? undefined,
            accessories: steps?.productInfo?.accessories ?? []
        });

        this.imagesForm.patchValue({
            front: steps?.images?.front ?? undefined,
            back: steps?.images?.back ?? undefined,
            bottom: steps?.images?.bottom ?? undefined,
            interior: steps?.images?.interior ?? undefined,
            additional: steps?.images?.additional ?? []
        });

        this.form.patchValue({
            id: steps?.id ?? undefined,
            referralCode: (this.route.snapshot.queryParams.referralCode || steps?.referralCode) ?? undefined,
            currentStep: steps?.currentStep ?? undefined
        });
    }

    private addAccessories(accessories?: Accessory[]) {
        this.accessoriesFormArray.clear();
        accessories.forEach((accessory) => {
            this.accessoriesFormArray.push(
                new FormGroup<AccessoryGroup>({
                    label: new FormControl(accessory.label),
                    isSelected: new FormControl(accessory.isSelected)
                })
            );
        });
    }

    private addAdditionalImages(images?: string[]) {
        if (!this.additionalImagesFormArray.length) {
            images.forEach((image) => {
                this.additionalImagesFormArray.push(new FormControl(image));
            });
        }
    }

    addAdditionalImage(img: any) {
        this.additionalImagesFormArray.push(new FormControl(img));
    }

    removeAdditionalImage(index: number) {
        this.additionalImagesFormArray.removeAt(index);
    }

    get additionalImagesFormArray(): FormArray<FormControl<string>> {
        return this.imagesForm.get('additional') as FormArray<FormControl<string>>;
    }

    get accessoriesFormArray(): FormArray<FormGroup<AccessoryGroup>> {
        return this.productInfoForm.get('accessories') as FormArray<FormGroup<AccessoryGroup>>;
    }

    async saveDraft() {
        try {
            await this.saveImages();
        } catch (error) {
            console.log('Error saving images', error);
        }
        const steps = await lastValueFrom(this.serviceApi.saveDraft(this.form.value));
        this.recentProducts = steps.recentProducts;
    }

    async saveForm() {
        try {
            await this.saveImages();
        } catch (error) {
            console.log('Error saving images', error);
        }
        const formValue = await lastValueFrom(this.serviceApi.saveSteps(this.form.value));
        this.recentProducts = formValue.recentProducts;
        this.resetStepsDetails();
        this.setApiFormData(formValue);
    }

    async saveImages() {
        await this.getFileUploadUrl(this.imagesForm.controls.front as FormControl<any>);
        await this.getFileUploadUrl(this.imagesForm.controls.back as FormControl<any>);
        await this.getFileUploadUrl(this.imagesForm.controls.bottom as FormControl<any>);
        await this.getFileUploadUrl(this.imagesForm.controls.interior as FormControl<any>);

        for (let i = 0; i < this.additionalImagesFormArray.length; i++) {
            const control = this.additionalImagesFormArray.at(i);
            await this.getFileUploadUrl(control as FormControl);
        }
    }

    async getFileUploadUrl(control: FormControl<any>) {
        if (control.value instanceof File) {
            const file = control.value;

            const formData = new FormData();
            formData.append('file', file);
            const uploadedFile = await firstValueFrom(this.fileService.upload(formData));

            control?.setValue(environment.apiUrl + uploadedFile.url);
        }
    }

    async resetStepsAndForm() {
        const value = await lastValueFrom(this.serviceApi.resetDraft());
        this.recentProducts = value.recentProducts;
        this.clearFormInformation();
        this.setApiFormData(value);
    }

    private clearFormInformation() {
        this.resetStepsDetails();
    }

    private resetStepsDetails() {
        this.additionalImagesFormArray.clear();
        this.displayErrorsInfoForm = false;
        this.displayErrorsImagesForm = false;
        this.saveAsDraft = false;
    }
}
